import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, ChevronDown, RefreshCw } from 'lucide-react'
import { useDomainAgenda } from '../hooks/useKnowledge'
import { compactCount } from './gardenCharts'

export function DomainAgenda({ compact = false }: { compact?: boolean }) {
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<string | null>(null)
  const query = useDomainAgenda(search, offset)
  const agenda = query.data
  if (query.isLoading)
    return (
      <section
        aria-label="Loading ontology domains"
        className="h-40 animate-pulse rounded-xl bg-slate-100"
      />
    )
  if (query.isError || !agenda?.available)
    return (
      <section
        className="rounded-xl border border-slate-200 p-5"
        aria-label="Ontology domain agenda"
      >
        <h2 className="font-serif text-xl">Ontology domains</h2>
        <p className="mt-2 text-sm text-slate-500">
          {agenda?.message || 'The domain agenda could not load.'}
        </p>
        <button className="mt-3 text-sm underline" onClick={() => query.refetch()}>
          Retry domain agenda
        </button>
      </section>
    )
  const visible = agenda.data.slice(0, 12)
  const positions = new Map(
    visible.map((d, i) => {
      const angle = (i / Math.max(1, visible.length)) * Math.PI * 2 - Math.PI / 2
      return [d.id, { x: 300 + Math.cos(angle) * 200, y: 155 + Math.sin(angle) * 112 }]
    })
  )
  const active = new Set(agenda.activeDomainIds)
  const rows = compact
    ? selected
      ? agenda.data.filter((d) => d.id === selected)
      : agenda.data.filter((d) => active.has(d.id)).slice(0, 3)
    : agenda.data
  return (
    <section
      className="overflow-hidden rounded-xl border border-arthaus-gold/25 bg-white"
      aria-label="Ontology domain agenda"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 p-5">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-arthaus-gold">
            The research agenda
          </p>
          <h2 className="mt-1 font-serif text-2xl">Shared structures. Evolving understanding.</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Overlapping communities guide research into concepts, techniques and their boundaries.
            Labels are proposed; semantic quality awaits independent review.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          aria-label="Refresh domain agenda"
          className="rounded-full border p-2"
        >
          <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-4 border-b border-slate-100 p-5 md:grid-cols-4">
        {[
          ['Proposed domains', agenda.summary?.domains],
          ['Overlapping concepts', agenda.summary?.overlappingConcepts],
          ['Selected work scopes', agenda.activeDomainIds?.length],
          ['Outside domain scopes', agenda.summary?.unassignedConcepts],
        ].map(([label, value]) => (
          <div key={String(label)}>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="mt-1 font-serif text-3xl">
              {typeof value === 'number' ? compactCount(value) : '—'}
            </p>
          </div>
        ))}
      </div>
      {visible.length > 1 && (
        <div className="bg-slate-50/50 px-4 pb-3 pt-4">
          <svg
            viewBox="0 0 600 310"
            className="mx-auto max-h-80 w-full"
            role="img"
            aria-label="Shared concept connections between visible research domains"
          >
            {(agenda.overlaps || []).map((e) => {
              const a = positions.get(e.source),
                b = positions.get(e.target)
              if (!a || !b) return null
              return (
                <line
                  key={`${e.source}-${e.target}`}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="currentColor"
                  className="text-arthaus-gold"
                  opacity={selected && selected !== e.source && selected !== e.target ? 0.04 : 0.2}
                  strokeWidth={Math.min(5, 1 + Math.log1p(e.shared) / 2)}
                >
                  <title>{e.shared.toLocaleString()} shared concepts</title>
                </line>
              )
            })}
            {visible.map((d) => {
              const p = positions.get(d.id)!
              return (
                <g
                  key={d.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Inspect domain ${d.label}`}
                  onClick={() => setSelected(d.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setSelected(d.id)
                    }
                  }}
                  className="cursor-pointer text-arthaus-gold outline-none focus:opacity-60"
                >
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={selected === d.id ? 22 : 16}
                    fill={active.has(d.id) ? 'currentColor' : 'white'}
                    stroke="currentColor"
                    strokeWidth={selected === d.id ? 3 : 1.5}
                    className="transition-all motion-reduce:transition-none"
                  />
                  <text x={p.x} y={p.y + 34} textAnchor="middle" fontSize="10" fill="currentColor">
                    {d.label.slice(0, 24)}
                    {d.label.length > 24 ? '…' : ''}
                  </text>
                  <title>
                    {d.label} · {d.metrics.concepts} concepts
                  </title>
                </g>
              )
            })}
          </svg>
          <p className="text-center text-[11px] text-slate-500">
            Select a domain to inspect it. Connections show shared concepts; positions are
            illustrative.
          </p>
        </div>
      )}
      {!compact && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b p-5">
          <input
            aria-label="Search ontology domains"
            placeholder="Find a concept or research domain…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setOffset(0)
              setSelected(null)
            }}
            className="w-full max-w-md rounded-md border px-3 py-2 text-sm"
          />
          <span className="text-xs text-slate-500">{agenda.total} matching domains</span>
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {rows.map((d) => (
          <article key={d.id} className="p-5">
            <button
              className="flex w-full items-start justify-between gap-4 text-left"
              aria-expanded={selected === d.id}
              onClick={() => setSelected(selected === d.id ? null : d.id)}
            >
              <span>
                <span className="font-serif text-lg">{d.label}</span>
                <span className="mt-1 block text-xs text-slate-500">
                  {d.resolutions && d.resolutions.length > 1
                    ? 'Shared across lenses · '
                    : d.resolutions?.some((r) => r < 1)
                      ? 'Broad lens · '
                      : d.resolutions?.length
                        ? 'Focused lens · '
                        : ''}
                  {d.metrics.concepts.toLocaleString()} concepts · {d.metrics.boundaryConcepts}{' '}
                  boundary members · {d.metrics.directArtworkReach?.toLocaleString() ?? 'Unknown'}{' '}
                  artworks with direct links
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2 text-[10px] uppercase tracking-wide text-arthaus-gold">
                {active.has(d.id) ? 'Selected scope' : d.state.replace(/_/g, ' ')}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${selected === d.id ? 'rotate-180' : ''}`}
                />
              </span>
            </button>
            {selected === d.id && (
              <div className="mt-4 space-y-4 text-sm">
                <p>
                  <span className="text-slate-500">Next action:</span> {d.nextAction}
                </p>
                <Link
                  to={`/app/knowledge/proposals?action=link&domainId=${encodeURIComponent(d.id)}`}
                  className="inline-flex text-xs underline"
                >
                  Review this domain’s proposals
                </Link>
                {!!d.evidence?.length && (
                  <div className="space-y-1 text-xs">
                    <p className="font-medium">Candidate literature alignments</p>
                    {d.evidence.map((e) => (
                      <p key={e.id}>
                        {/^https?:\/\//.test(e.sourceUri) ? (
                          <a
                            href={e.sourceUri}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            {e.name}
                          </a>
                        ) : (
                          e.name
                        )}{' '}
                        · {e.scope || 'scope unspecified'}
                        {e.targetId ? ` · ${e.targetId}` : ''}
                        {e.claimRelation ? ` · ${e.claimRelation}` : ''}
                        {e.polarity ? ` · ${e.polarity.replace(/_/g, ' ')}` : ''} ·{' '}
                        {(e.semanticEntailment || 'pending review').replace(/_/g, ' ')}
                      </p>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  Priority considers {d.priorityReasons.join(', ')}. {d.pendingReviewConcepts}{' '}
                  concepts have eligible review work.
                </p>
                <div className="flex flex-wrap gap-2">
                  {d.representatives.map((c) => (
                    <Link
                      key={c.ucid}
                      to={`/app/knowledge/${c.ucid}`}
                      className="inline-flex items-center gap-1 rounded-full border border-arthaus-gold/30 px-3 py-1 text-xs"
                    >
                      {c.name}
                      <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  ))}
                </div>
                {d.history.length ? (
                  <div className="space-y-2">
                    {d.history
                      .slice(-3)
                      .reverse()
                      .map((h) => (
                        <div key={h.runId} className="rounded-md bg-slate-50 p-3 text-xs">
                          <p>
                            {h.at.slice(0, 10)} · {h.reviewed} reviewed · {h.proposed} proposed ·{' '}
                            {h.status}
                          </p>
                          {!!h.samples?.length && (
                            <div className="mt-2 space-y-2">
                              {h.samples.map((sample, i) => (
                                <div key={i} className="border-l border-arthaus-gold/40 pl-3">
                                  <p className="font-medium">
                                    {sample.names.join(' ↔ ')} · {sample.decision}
                                    {sample.relationType
                                      ? ` · ${sample.relationType.replace(/_/g, ' ')}`
                                      : ''}
                                  </p>
                                  <p className="mt-1">{sample.reason}</p>
                                  {sample.heldReason && (
                                    <p className="mt-1 text-amber-800">Held: {sample.heldReason}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          <p className="mt-1 text-slate-500">
                            Independent semantic review: {h.semanticValidation.replace(/_/g, ' ')}
                          </p>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">
                    No review outcomes recorded for this scope yet.
                  </p>
                )}
              </div>
            )}
          </article>
        ))}
        {!rows.length && compact && (
          <p className="p-5 text-sm text-slate-500">
            No scopes selected for the current research pass. Explore the full domain registry
            below.
          </p>
        )}
        {!agenda.data.length && !compact && (
          <p className="p-5 text-sm text-slate-500">No domains match this search.</p>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t p-5 text-xs text-slate-500">
        <span>
          Graph {agenda.graphRunId} · Analysis{' '}
          {agenda.observedAt ? new Date(agenda.observedAt).toLocaleString() : 'unknown'}
        </span>
        {compact ? (
          <Link
            to="/app/knowledge/domains"
            className="inline-flex items-center gap-1 font-medium text-slate-800 underline underline-offset-4"
          >
            Explore all {agenda.summary?.domains ?? agenda.total} research domains{' '}
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        ) : (
          <span className="flex gap-4">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - 20))}
              className="underline disabled:opacity-30"
            >
              Previous
            </button>
            <button
              disabled={offset + 20 >= (agenda.total || 0)}
              onClick={() => setOffset(offset + 20)}
              className="underline disabled:opacity-30"
            >
              Next
            </button>
          </span>
        )}
      </div>
    </section>
  )
}
